import { FC, useState } from 'react';
// Types
import Colors from 'app/types/Colors';
// Redux
import { useAppDispatch, useAppSelector } from 'app/hooks/useStore';
// Actions
import { DMSDocumentPagesActions } from 'app/store/DMSDocumentPages/DMSDocumentPages.slice';
// Selectors
import { selectPagesFilter } from 'app/store/DMSDocumentPages/DMSDocumentPages.selectors';
import { selectEpisodeTypesAsOptions, selectEpisodeLabelsAsOptions, selectEpisodeAuthorsAsOptions } from 'app/store/Episodes/Episodes.selectors';
// Mui
import { Box } from '@mui/material';
// Components
import { Autocomplete } from 'app/components/Mui/Autocompletes';
import ColumnSwitcher from 'app/components/ColumnSwitcher';
import ColorPalette from 'app/components/ColorPalette';
import TagPicker from 'app/components/TagPicker';
// i18next
import { useTranslation } from 'react-i18next';

const PagesFilter:FC = () => {
  const { t } = useTranslation();
  // Dispatch
  const dispatch = useAppDispatch();
  // State
  const filter:any = useAppSelector(selectPagesFilter);
  const episodeTypesOptions = useAppSelector(selectEpisodeTypesAsOptions);
  const episodeLabelsOptions = useAppSelector(selectEpisodeLabelsAsOptions);
  const episodeAuthors = useAppSelector(selectEpisodeAuthorsAsOptions);

  const [ stateFilter, setStateFilter ] = useState(filter);

  const handleAutocompleteChange = (field:any) => (newValue:any) => {
    setStateFilter((prevState:any) => ({ ...prevState, [field]: newValue }));
    dispatch(DMSDocumentPagesActions.setPagesFilter({ field, value: newValue || [] }));
  }

  const handleColorClick = (color:Colors|string) => {
    const nextColors = stateFilter.colors.includes(color)
      ? stateFilter.colors.filter((c:Colors | string) => c !== color)
      : [...stateFilter.colors, color]
    ;
    setStateFilter((prevState:any) => ({ ...prevState, colors: nextColors }));
    dispatch(DMSDocumentPagesActions.setPagesFilter({ field: 'colors', value: nextColors }));
  }

  const handleClickTag = (nextTag:string | null) => {
    const idx = stateFilter.tags.indexOf(nextTag);
    const nextTags = idx !== -1 ? stateFilter.tags.filter((t:string, i:number) => i !== idx) : [...stateFilter.tags, nextTag];
    setStateFilter((prevState:any) => ({ ...prevState, tags: nextTags }));
    dispatch(DMSDocumentPagesActions.setPagesFilter({ field: 'tags', value: nextTags }));
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4, p: 4 }}>
      <Autocomplete
        multiple
        size="small"
        value={stateFilter.episodeTypes}
        onChange={handleAutocompleteChange('episodeTypes')}
        options={episodeTypesOptions || []}
        sx={{ width: 320 }}
        TextFieldProps={{
          label: t('labels.episodeTypes'),
          margin: "none"
        }}
      />
      <Autocomplete
        multiple
        size="small"
        value={stateFilter.episodeLabels}
        onChange={handleAutocompleteChange('episodeLabels')}
        options={episodeLabelsOptions || []}
        sx={{ width: 320 }}
        TextFieldProps={{
          label: t('labels.episodeLabels'),
          margin: "none"
        }}
      />
      <Autocomplete
        multiple
        size="small"
        value={stateFilter.episodeAuthors}
        onChange={handleAutocompleteChange('episodeAuthors')}
        options={episodeAuthors || []}
        sx={{ width: 320 }}
        TextFieldProps={{
          label: t('labels.episodeAuthors'),
          margin: "none"
        }}
      />
      <ColumnSwitcher />
      <TagPicker
        id="presenter-workspace-tags"
        onClick={handleClickTag}
        noTagVisible={true}
        activeTags={stateFilter.tags}
      />
      <ColorPalette activeColors={stateFilter.colors}>
        {(color) => (
          <ColorPalette.Item
            key={color}
            color={color}
            onClick={handleColorClick}
          />
        )}
      </ColorPalette>
    </Box>
  )
}

export default PagesFilter;
